import { v4 as uuidv4 } from 'uuid';

// Initialize dataLayer if it doesn't exist
const initializeDataLayer = () => {
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  }
};

// Check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

// Get attribution data from localStorage with optional filtering
export const getAttributionData = (requiredParams = null) => {
  if (!isBrowser()) return {};
  
  const allAttributionKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ref'];
  const keysToCheck = requiredParams || allAttributionKeys;
  
  return keysToCheck.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value) acc[key] = value;
  }, {});
};

// Clean object by removing null/undefined values
const cleanEventData = (data) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== null && value !== undefined)
  );
};

// Base event pusher
export const gtmPushEvent = (event) => {
  if (!isBrowser() || !window.dataLayer) {
    console.warn('GTM: dataLayer not available');
    return;
  }
  
  const cleanEvent = cleanEventData(event);
  window.dataLayer.push(cleanEvent);
  
  console.log('GTM Event pushed:', cleanEvent);
};

// Create base event with common properties
const createBaseEvent = (eventName, additionalData = {}) => ({
  event: eventName,
  timestamp: new Date().toISOString(),
  session_id: sessionStorage.getItem('session_id') || uuidv4(),
  ...additionalData
});

// Track page views - gets all attribution data
export const gtmPageView = (additionalData = {}) => {
  if (!isBrowser() || !window.dataLayer) {
    console.warn('GTM: dataLayer not available');
    return;
  }
  
  const eventData = createBaseEvent('page_view', {
    ...additionalData,
    ...getAttributionData()
  });
  
  gtmPushEvent(eventData);
};

// Track user login
export const gtmTrackLogin = (user_id, method = 'email') => {
  const eventData = createBaseEvent('login', {
    method,
    user_id,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  gtmPushEvent(eventData);
};

// Track user registration
export const gtmTrackRegistration = (user_id, method = 'email') => {
  const eventData = createBaseEvent('sign_up', {
    method,
    user_id,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  gtmPushEvent(eventData);
};

// Track form submissions
export const gtmTrackFormSubmit = (form_name, form_step = null) => {
  const eventData = createBaseEvent('form_submit', {
    form_name,
    form_step,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  gtmPushEvent(eventData);
};

// Track button clicks
export const gtmTrackButtonClick = (button_name, section = null) => {
  const eventData = createBaseEvent('click', {
    button_name,
    section
  });
  
  gtmPushEvent(eventData);
};

// Generic event tracker for custom events
export const gtmTrackCustomEvent = (eventName, eventData = {}) => {
  const customEventData = createBaseEvent(eventName, eventData);
  gtmPushEvent(customEventData);
};

// Initialize GTM on module load
initializeDataLayer();